<!--
  BulkVisibilityModal.vue -- Phase 4.4 bulk set-visibility modal.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.4 bulk visibility modal.

  Modal-on-demand: parent wraps with v-model. Two radio choices:

    * "Show on storefront" -- maps to visible=true
    * "Hide from storefront" -- maps to visible=false

  On submit the component emits ``submit`` with ``{ visible: bool }``;
  the parent is responsible for calling the API for each selected
  CampaignProduct id. The legacy ``hide_from_storefront`` sync is
  handled at the model layer (CampaignProduct ``before_update``
  listener) so we only need to set ``visible``.

  Per the implementation plan section 4.5 (lost-functionality watch):
  "the Bulk Visibility action must respect existing semantics
  (hide_from_storefront sync, etc.)" -- the sync is automatic at the
  model layer; this modal does not need to do anything special.
-->
<template>
    <div
        v-if="modelValue"
        class="bulk-visibility-modal"
        data-testid="bulk-visibility-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-visibility-modal-title"
    >
        <div class="bulk-visibility-modal__backdrop" @click="onCancel"></div>
        <div class="bulk-visibility-modal__panel">
            <h2
                id="bulk-visibility-modal-title"
                class="bulk-visibility-modal__title"
            >
                Set visibility
            </h2>
            <p class="bulk-visibility-modal__lede">
                Apply to {{ selectedCount }}
                {{ selectedCount === 1 ? 'product' : 'products' }}.
            </p>
            <div
                class="bulk-visibility-modal__choices"
                role="radiogroup"
                aria-label="Visibility choice"
            >
                <label
                    class="bulk-visibility-modal__choice"
                    :class="{
                        'bulk-visibility-modal__choice--active': visible,
                    }"
                    data-testid="bulk-visibility-visible"
                    @click="visible = true"
                >
                    <input
                        type="radio"
                        name="bulk-visibility"
                        :checked="visible"
                        @change="visible = true"
                    />
                    <span class="bulk-visibility-modal__choice-text">
                        Show on storefront
                    </span>
                </label>
                <label
                    class="bulk-visibility-modal__choice"
                    :class="{
                        'bulk-visibility-modal__choice--active': !visible,
                    }"
                    data-testid="bulk-visibility-hidden"
                    @click="visible = false"
                >
                    <input
                        type="radio"
                        name="bulk-visibility"
                        :checked="!visible"
                        @change="visible = false"
                    />
                    <span class="bulk-visibility-modal__choice-text">
                        Hide from storefront
                    </span>
                </label>
            </div>
            <div class="bulk-visibility-modal__actions">
                <button
                    type="button"
                    class="bulk-visibility-modal__btn"
                    data-testid="bulk-visibility-cancel"
                    @click="onCancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="bulk-visibility-modal__btn bulk-visibility-modal__btn--primary"
                    data-testid="bulk-visibility-submit"
                    @click="onSubmit"
                >
                    Apply
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BulkVisibilityModal',
    props: {
        modelValue: {
            type: Boolean,
            required: true,
            default: false,
        },
        selectedCount: {
            type: Number,
            required: true,
            default: 0,
        },
    },
    emits: ['update:modelValue', 'submit'],
    data() {
        return {
            // Default to "show on storefront" -- the safer, more
            // common bulk action when a Builder selects a clump of
            // newly-stubbed CPs that all need to come live.
            visible: true,
        }
    },
    watch: {
        modelValue(open) {
            if (open) {
                // Reset on each open so a previous "Hide" pick does not
                // leak into the next session.
                this.visible = true
            }
        },
    },
    methods: {
        onSubmit() {
            this.$emit('submit', { visible: this.visible })
        },
        onCancel() {
            this.$emit('update:modelValue', false)
        },
    },
}
</script>

<style scoped>
.bulk-visibility-modal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
}
.bulk-visibility-modal__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
}
.bulk-visibility-modal__panel {
    position: relative;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
    padding: 24px;
    width: min(420px, 90vw);
    z-index: 1;
}
.bulk-visibility-modal__title {
    margin: 0 0 8px;
    font-size: 18px;
    color: #1e293b;
}
.bulk-visibility-modal__lede {
    margin: 0 0 16px;
    font-size: 13px;
    color: #475569;
}
.bulk-visibility-modal__choices {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.bulk-visibility-modal__choice {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
}
.bulk-visibility-modal__choice--active {
    border-color: #6366f1;
    background: #eef2ff;
}
.bulk-visibility-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
}
.bulk-visibility-modal__btn {
    appearance: none;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
}
.bulk-visibility-modal__btn--primary {
    background: #6366f1;
    border-color: #6366f1;
    color: #fff;
}
.bulk-visibility-modal__btn--primary:hover {
    background: #4f46e5;
}
</style>
